import {
  CustomLogicEvent,
  NotFoundError,
  sendNotification,
  useApiClient,
  useAsyncCache,
  useEntityMetadataIncludeExternal,
  useFormMetadata,
  useModuleBaseMetadata,
  useModuleFederationContext,
  useNavigation,
  useSignalr,
  useTranslation,
  useUrlContext,
} from '@msdk/common'
import {
  ACL_DEFAULT,
  Entity,
  EntityAcl,
  EntityContext,
  EntityModel,
  EntityOperation,
  findNode,
  formCreateControls,
  FormGroupDefinition,
  FormModel,
  FormNode,
  formTraverse,
  GenericFieldValue,
  OptionsetValue,
  ServerOperationResult,
  ValidationMessageGroup,
} from '@msdk/definitions'
import { useEffect, useState } from 'react'
import { useRecoilState_TRANSITION_SUPPORT_UNSTABLE } from 'recoil'

import { CustomLogicInvoker } from '../components/DynamicForm/custom-logic-invoker'
import { $entityCollection, CollectionEntry } from '../state'
import { applyServerMessages, messagesFromEntityOperation, stringFormat } from '../utils'
import { useComponentModal, useDialogInputModal, useDialogModal, useFormModal, useViewModal } from './modal'

export type SaveResult = EntityOperation & { event?: CustomLogicEvent }
export type ServerError = undefined | 404

function useFormBase(
  entityName: string,
  formName: string,
  id: string,
  prefilledFIelds?: Record<string, GenericFieldValue>,
  containerEntity?: Entity,
) {
  const baseMetadata = useModuleBaseMetadata(entityName, formName)
  const entityModel = useEntityMetadataIncludeExternal(entityName, formName) as EntityModel
  const formModel = useFormMetadata(entityName, formName) as FormModel
  const { context } = useUrlContext()

  const [form, setForm] = useState<FormNode>()
  const [entityLoaded, setEntityLoaded] = useState<boolean>(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [entity, setEntity] = useState<Entity | null>({ internalId: crypto.randomUUID() } as any)
  const [additionalEntities, setAdditionalEntities] = useState<Entity[]>([])
  const [acl, setAcl] = useState<EntityAcl>(ACL_DEFAULT)
  const [entityCollection, setEntityCollection] = useRecoilState_TRANSITION_SUPPORT_UNSTABLE($entityCollection)
  const [serverError, setServerError] = useState<ServerError>()

  // for validation in CreateNew, PostLoad hooks
  const [hooksServerMessages, setHooksServerMessages] = useState<ValidationMessageGroup[]>([])

  const client = useApiClient()
  const modules = useModuleFederationContext()
  const apiClient = useApiClient()
  const formModal = useFormModal()
  const viewModal = useViewModal()
  const componentModal = useComponentModal()
  const dialogModal = useDialogModal()
  const dialogInputModal = useDialogInputModal()
  const asyncCache = useAsyncCache()
  const navigate = useNavigation()
  const registerTopic = useSignalr()
  const t = useTranslation()

  const [invoker, setInvoker] = useState<CustomLogicInvoker>()

  /**
   * resolve parentId for the current entity
   */
  const getParentId = () => {
    // eslint-disable-next-line prefer-destructuring
    const parent = entityModel.parent
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const id = context[parent]
    if (id) {
      return id
    } else if (containerEntity) {
      return containerEntity.id
    }
  }

  const fetchEntity = async (customId?: string, customParent?: string) => {
    const entityId = customId ?? id
    let response: EntityOperation | null = null
    if (entityId === 'Create') {
      const parentId = customParent ?? getParentId()
      response = await client.entity.createEntity(
        baseMetadata?.msProvider as string,
        entityModel,
        parentId,
        true,
        formModel,
        prefilledFIelds,
      )
    } else {
      try {
        if (entityModel.singleton)
          response = await client.entity.getSingleton(
            baseMetadata?.msProvider as string,
            entityId,
            entityModel,
            formModel,
          )
        else
          response = await client.entity.getEntity(
            baseMetadata?.msProvider as string,
            entityId,
            entityModel,
            true,
            formModel,
          )
      } catch (error) {
        if (error instanceof NotFoundError) {
          setServerError(404)
        } else {
          throw error
        }
      }
    }

    if (response) {
      setHooksServerMessages(response.messages?.entities ?? [])

      setEntity((current) => {
        return {
          ...response.entities[0],
          internalId: current?.internalId,
          isInCreation: response.operationArgs?.isInCreation ?? false,
        }
      })

      setAdditionalEntities(() => {
        const cloned = [...response.entities]
        const res = cloned.shift()
        const additional: Entity[] = res ? cloned : []
        return additional
      })

      const cloned = [...response.entities]
      cloned.shift()

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const entityContext = new EntityContext(response.entities[0], null as any, cloned)

      // Merge all acl into one single object, for each acl
      // resolve the alias for the given entity and prepend it to the name of
      // the field, for all the arrays of special fields.
      const aclMetadata = response.metadataList[0]
      response.entities.forEach((e, index) => {
        const alias = entityContext.getAliasFromEntityId(e.id)
        // The entity with empty alias it the main entity, we don't need to process it
        if (alias && alias != '') {
          const metadata = response.metadataList[index]
          metadata.invisibleFields = metadata.invisibleFields.map((i) => `${alias}.${i}`)
          metadata.sensitiveFields = metadata.sensitiveFields.map((i) => `${alias}.${i}`)
          metadata.nonWritableFields = metadata.nonWritableFields.map((i) => `${alias}.${i}`)
          aclMetadata.invisibleFields = [...aclMetadata.invisibleFields, ...metadata.invisibleFields]
          aclMetadata.sensitiveFields = [...aclMetadata.sensitiveFields, ...metadata.sensitiveFields]
          aclMetadata.nonWritableFields = [...aclMetadata.nonWritableFields, ...metadata.nonWritableFields]
        }
      })
      setAcl(aclMetadata)
    }
  }

  useEffect(() => {
    return () => {
      invoker?.onDestroy()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    fetchEntity().catch(console.error)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityName, formName, id])

  useEffect(() => {
    if (modules.loadedModules[baseMetadata?.msProvider as string]) {
      setInvoker(
        new CustomLogicInvoker(
          [...entityModel.customLogics, ...formModel.customLogics],
          modules.loadedModules[baseMetadata?.msProvider as string],
          apiClient,
          { formModal, viewModal, componentModal, dialogModal, dialogInputModal },
          t,
          asyncCache,
          context,
          navigate,
          registerTopic,
          onReloadEntity,
          rebindEntity,
        ),
      )
    }
  }, [modules])

  useEffect(() => {
    const entityContext = entity?.fields
      ? new EntityContext(entity as Entity, form as FormNode, additionalEntities, formModel)
      : undefined
    const firstChild = formCreateControls(
      formModel.layout,
      entityModel as EntityModel,
      formModel,
      formModel.name,
      entityContext,
    )
    const rootControl: FormNode = {
      id: 'ROOT',
      firstChild,
      value: {
        data: null,
        type: 'Group',
        validationMessages: [],
        validationParams: [],
        state: 'Enabled',
        validationState: 'Valid',
        isDirty: false,
        visibility: 'Visible',
        validationFlags: 0,
        entityName: '',
        formDefinition: {
          clientStyle: '',
          columnSpan: 1,
          type: 'Group',
          columns: 1,
        } as FormGroupDefinition,
        rootFormModelName: formModel.name,
      },
    }

    // if there is a tree already built whe are in this use effect because of a save
    // in this case map existing validation messages (validations coming from server after save)
    // from previous form tree to avoid losing them
    if (form) {
      formTraverse(rootControl, (node) => {
        const oldNode = findNode(form, node.id)
        if (oldNode) {
          node.value.validationMessages = oldNode.value.validationMessages
          node.value.validationParams = oldNode.value.validationParams
        }
      })
    }

    if (entityContext && hooksServerMessages?.length) {
      applyServerMessages(rootControl, hooksServerMessages, entityContext)
    }

    setForm(rootControl)
    if (entity && entity.fields) {
      setEntityLoaded(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityModel, formModel, entity, hooksServerMessages])

  const onSave = async (actionId?: string): Promise<SaveResult> => {
    const entry = entityCollection[(entity as Entity).internalId as string]
    const event: CustomLogicEvent = {
      preventPostSaveNavigation: false,
      preventSuccessNotificaton: false,
      preventFailureNotification: false,
    }

    const preSaveContext = new EntityContext(entry.entity, form as FormNode, entry.additionalEntities)
    const presaveCanContinue = await invoker?.preSave(preSaveContext, event)

    if (presaveCanContinue) {
      const canContinue = await invoker?.save(preSaveContext, event)
      const preSaveUnboxed = preSaveContext.unbox()

      if (canContinue) {
        const res = await client.entity.saveEntities(
          baseMetadata?.msProvider as string,
          entityModel,
          [preSaveUnboxed.entity, ...preSaveUnboxed.additionalEntities],
          formModel,
          actionId,
        )

        const entityDescription = t(entityModel.idml, entityModel.description)
        if (res.result === ServerOperationResult.Success) {
          const mainEntity = res.entities[0]
          const otherEntities = res.entities.slice(1)
          const entityContext = new EntityContext(mainEntity, form as FormNode, otherEntities)
          await invoker?.postSave(entityContext, event)
          const unboxed = entityContext.unbox()

          const IDML = id === 'Create' ? 'LBL_CREATE_SUCCESS' : 'LBL_UPDATE_SUCCESS'
          const description = id === 'Create' ? '{0} was created successfully' : 'Changes were saved successfully'
          const message = stringFormat(t(IDML, description), entityDescription)
          setEntity((current) => {
            return {
              ...unboxed.entity,
              internalId: current?.internalId,
            }
          })
          setAdditionalEntities(unboxed.additionalEntities)
          setAcl(res.metadataList[0])

          applyServerMessages(form, res.messages.entities, entityContext)
          setForm({ ...(form as FormNode) })

          if (!event.preventPostSaveNavigation && !event.preventSuccessNotificaton) {
            sendNotification({ message, status: 'success' })
          }
        } else {
          setEntity((current) => {
            return {
              ...preSaveUnboxed.entity,
              internalId: current?.internalId,
            }
          })
          setAdditionalEntities([...preSaveUnboxed.additionalEntities])
          const errorIDML = id === 'Create' ? 'LBL_CREATE_ERROR' : 'LBL_UPDATE_ERROR'
          const errorDescription = id === 'Create' ? '{0} was not created' : 'Changes were not saved'
          const errorMessage = stringFormat(t(errorIDML, errorDescription), entityDescription)

          const serverMessages = messagesFromEntityOperation(res)

          await invoker?.onSaveFailure(serverMessages, event)

          if (!event.preventFailureNotification) {
            if (serverMessages.length) {
              const translated = serverMessages.map((msg) => `${t(msg.idml, msg.description)}`).join('\n')
              sendNotification({ message: translated, status: 'error' })
            } else {
              applyServerMessages(form, res.messages.entities, preSaveContext)
              setForm({ ...(form as FormNode) })
              sendNotification({ message: errorMessage, status: 'error' })
            }
          }
        }
        return { ...res, event }
      }
    }
    return { result: ServerOperationResult.Failure, event } as SaveResult
  }

  const onMoveToBin = async () => {
    const entry = entityCollection[(entity as Entity).internalId as string]
    const presaveContext = new EntityContext(entry.entity, form as FormNode, entry.additionalEntities)
    const presaveUnboxed = presaveContext.unbox()

    const cloned = { ...presaveUnboxed.entity }
    const statusField = new OptionsetValue('INACTIVE', 'ENTITY_STATUS')
    const reasonField = new OptionsetValue('LOGICALLY_DELETE', 'DEACTIVATION_REASON')
    cloned.fields = {}
    cloned.fields['ENTITY_STATUS'] = statusField
    cloned.fields['DEACTIVATION_REASON'] = reasonField

    if (entityModel.extendedBy != null && entityModel.extendedBy != '') {
      cloned.fields['EXTENDED_BY'] = presaveUnboxed.entity.fields['EXTENDED_BY']
      cloned.fields['EXTENDED_BY_PARENTID'] = presaveUnboxed.entity.fields['EXTENDED_BY_PARENTID']
    }

    const res = await client.entity.forceSaveEntities(baseMetadata?.msProvider as string, entityModel, [cloned])

    if (res.result === ServerOperationResult.Success) {
      const mainEntity = res.entities[0]
      const otherEntities = res.entities.slice(1)
      const entityContext = new EntityContext(mainEntity, form as FormNode, otherEntities)
      const unboxed = entityContext.unbox()

      setEntity((current) => {
        return {
          ...unboxed.entity,
          internalId: current?.internalId,
        }
      })
      setAdditionalEntities(presaveUnboxed.additionalEntities)
      setAcl(res.metadataList[0])
      applyServerMessages(form, res.messages.entities, entityContext)
      setForm({ ...(form as FormNode) })
      return res
    } else {
      const translated = t('CMPSR_TOBIN_ERROR', 'Moving to bin failed.')
      sendNotification({ message: translated, status: 'error' })
      applyServerMessages(form, res.messages.entities, presaveContext)
      setForm({ ...(form as FormNode) })
      return res
    }
  }

  const onRestore = async () => {
    const entry = entityCollection[(entity as Entity).internalId as string]
    const presaveContext = new EntityContext(entry.entity, form as FormNode, entry.additionalEntities)
    const presaveUnboxed = presaveContext.unbox()

    const cloned = { ...presaveUnboxed.entity }
    const statusField = new OptionsetValue('ACTIVE', 'ENTITY_STATUS')
    cloned.fields = {}
    cloned.fields['ENTITY_STATUS'] = statusField
    cloned.fields['DEACTIVATION_REASON'] = null

    if (entityModel.extendedBy != null && entityModel.extendedBy != '') {
      cloned.fields['EXTENDED_BY'] = presaveUnboxed.entity.fields['EXTENDED_BY']
      cloned.fields['EXTENDED_BY_PARENTID'] = presaveUnboxed.entity.fields['EXTENDED_BY_PARENTID']
    }

    const res = await client.entity.forceSaveEntities(baseMetadata?.msProvider as string, entityModel, [cloned])

    if (res.result === ServerOperationResult.Success) {
      const mainEntity = res.entities[0]
      const otherEntities = res.entities.slice(1)
      const entityContext = new EntityContext(mainEntity, form as FormNode, otherEntities)
      const unboxed = entityContext.unbox()

      setEntity((current) => {
        return {
          ...unboxed.entity,
          internalId: current?.internalId,
        }
      })
      setAdditionalEntities(presaveUnboxed.additionalEntities)
      setAcl(res.metadataList[0])
      applyServerMessages(form, res.messages.entities, entityContext)
      setForm({ ...(form as FormNode) })
      return res
    } else {
      const translated = t('CMPSR_TOBIN_ERROR', 'Moving to bin failed.')
      sendNotification({ message: translated, status: 'error' })
      applyServerMessages(form, res.messages.entities, presaveContext)
      setForm({ ...(form as FormNode) })
      return res
    }
  }

  const onDelete = async () => {
    return await client.entity.deleteEntity(baseMetadata?.msProvider as string, entityName, id, formName)
  }

  const onReloadEntity = async (customId?: string, customParent?: string) => {
    /**
     * setting to false here, once the entity is loaded it will be set to true
     * by the effect that will build the new form nodes
     */
    setEntityLoaded(false)
    try {
      await fetchEntity(customId, customParent)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error)
    }
  }

  const rebindEntity = (entityContext: EntityContext, pendingChanges = true) => {
    const stableInternalId = entity?.internalId // Capture before any state updates
    const unboxed = entityContext.unbox()
    setEntity((current) => {
      return {
        ...unboxed.entity,
        internalId: current?.internalId,
      }
    })
    setAdditionalEntities(unboxed.additionalEntities)

    setEntityCollection((current) => {
      const idStable = stableInternalId as string
      // If the incoming entity had a different internalId, clean up the stale entry so that
      // useEntityFromCollection (which searches by server entity.id) cannot accidentally return it
      const staleKey = unboxed.entity.internalId as string
      const base: Record<string, CollectionEntry> =
        staleKey && staleKey !== idStable
          ? Object.fromEntries(Object.entries(current).filter(([k]) => k !== staleKey))
          : current
      const currentEntry = base[idStable]
      return {
        ...base,
        [idStable]: {
          entity: { ...unboxed.entity, internalId: idStable },
          additionalEntities: unboxed.additionalEntities,
          rootNode: form as FormNode,
          pendingChanges,
          acl: currentEntry?.acl,
        },
      }
    })
  }

  return {
    entityModel,
    formModel,
    form,
    entity,
    additionalEntities,
    entityLoaded,
    acl,
    invoker,
    onSave,
    onMoveToBin,
    onRestore,
    onDelete,
    onReloadEntity,
    rebindEntity,
    serverError,
  }
}

/**
 * The `useForm` function is a custom hook in TypeScript that handles form management and data
 * retrieval for a specific entity and form.
 * @param {string} entityName - The `entityName` parameter is a string that represents the name of the
 * entity for which the form is being used. It is used to fetch the entity metadata and base metadata.
 * @param {string} formName - The `formName` parameter in the `useForm` function is a string that
 * represents the name of the form. It is used to retrieve the metadata for the specific form from the
 * server.
 * @param {string} id - The `id` parameter is the identifier of the entity. It can be used to fetch an
 * existing entity or create a new one. If the value of `id` is "Create", it indicates that a new
 * entity should be created. Otherwise, it is assumed to be the identifier of an existing
 * @returns an object with the following properties:
 */
export function useForm(
  entityName: string,
  formName: string,
  id: string,
  prefilledFIelds?: Record<string, GenericFieldValue>,
) {
  const {
    entityModel,
    formModel,
    form,
    entity,
    additionalEntities,
    entityLoaded,
    acl,
    invoker,
    onSave,
    onMoveToBin,
    onRestore,
    onDelete,
    onReloadEntity,
    rebindEntity,
    serverError,
  } = useFormBase(entityName, formName, id, prefilledFIelds)

  const onSavePage = async (actionId?: string) => {
    return await onSave(actionId)
  }

  return {
    entityModel,
    formModel,
    form,
    entity,
    additionalEntities,
    entityLoaded,
    onSave: onSavePage,
    acl,
    invoker,
    onMoveToBin,
    onRestore,
    onDelete,
    onReloadEntity,
    rebindEntity,
    serverError,
  }
}

/**
 * The `useModalForm` function is a custom hook in TypeScript that handles the state and logic for a
 * modal form component, including fetching entity data, creating or updating entities, and managing
 * form state.
 * @param {string} entityName - The `entityName` parameter is a string that represents the name of the
 * entity for which the modal form is being used.
 * @param {string} formName - The `formName` parameter in the `useModalForm` function is a string that
 * represents the name of the form to be used. It is used to retrieve the metadata and layout
 * information of the form from the server.
 * @param {string} id - The `id` parameter is used to identify the entity that the modal form is being
 * used for. It can have one of the following values:
 * @returns The function `useModalForm` returns an object with the following properties:
 */
export function useModalForm(
  entityName: string,
  formName: string,
  id: string,
  prefilledFIelds?: Record<string, GenericFieldValue>,
  containerEntity?: Entity,
) {
  const {
    entityModel,
    formModel,
    form,
    entity,
    additionalEntities,
    entityLoaded,
    acl,
    invoker,
    onSave,
    onMoveToBin,
    onRestore,
    onDelete,
    onReloadEntity,
    rebindEntity,
    serverError,
  } = useFormBase(entityName, formName, id, prefilledFIelds, containerEntity)

  return {
    entityModel,
    formModel,
    form,
    entity,
    additionalEntities,
    entityLoaded,
    onSave,
    acl,
    invoker,
    onMoveToBin,
    onRestore,
    onDelete,
    onReloadEntity,
    rebindEntity,
    serverError,
  }
}
